/**
 * DocumentsStep — Step 4 of the ProfileComplete v2.0 wizard.
 *
 * Industry-standard list pattern (Stripe Connect / Razorpay / Plaid):
 *   - One row per document, not card grid (10 docs in ~640px not ~1200px)
 *   - Left: status icon (◯ pending · ⏳ uploading · ✓ done · ⚠ error)
 *   - Middle: doc name + format/size hint OR uploaded filename
 *   - Right: single action — Upload / View+Replace+Delete trio
 *   - Inline error renders below the row when file-type validation fails
 *
 * Strict file-type guarantee:
 *   - FileUpload's native `accept` filters the OS file picker (UI hint)
 *   - validateFileType + validateFileSize pre-check on every selection
 *   - Wrong type / too big → error renders inline, no network request fired
 *
 * Skill compliance:
 *   - Section panel pattern matches BasicInfoStep / UserTypeStep / EstablishmentStep
 *   - Hard rule 6 (no hex), P5 inline errors, P15 progress strip
 */
import { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { validateFileType, validateFileSize } from './validators';

const formatBytes = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
};

/* SectionPanel — PrimeReact Panel with a slim custom header.
   Matches BasicInfoStep / UserTypeStep / EstablishmentStep. */
const SectionPanel = ({ icon, title, subtitle, children, className = '' }) => {
    const headerContent = (
        <div className="flex align-items-center gap-2">
            {icon && (
                <div
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{
                        width: '1.75rem',
                        height: '1.75rem',
                        backgroundColor: 'var(--primary-50)',
                        color: 'var(--primary-color)',
                        fontSize: '0.85rem',
                    }}
                >
                    <i className={`pi ${icon}`} aria-hidden="true" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-900 font-semibold text-sm">{title}</div>
                {subtitle && (
                    <div className="text-500 font-normal" style={{ fontSize: '0.7rem', marginTop: '0.1rem' }}>
                        {subtitle}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Panel
            header={headerContent}
            toggleable={false}
            className={classNames('mb-3', className)}
            pt={{
                header: { className: 'py-2 px-3' },
                content: { className: 'p-3' },
            }}
        >
            {children}
        </Panel>
    );
};

const DocumentsStep = ({
    requiredDocuments = [],
    uploadedDocuments = {},
    uploadingDocument,
    replacingDocument,
    establishmentTypeChosen,
    onUpload,
    onReplaceClick,
    onCancelReplace,
    onDelete,
    onView,
    saving = false,
}) => {
    // Per-row validation errors (clears when user picks another file)
    const [docErrors, setDocErrors] = useState({});

    /* ── Group docs by category ─────────────────────────────────────── */
    const groups = requiredDocuments.reduce((acc, d) => {
        const key = d.category || 'Documents';
        if (!acc[key]) acc[key] = [];
        acc[key].push(d);
        return acc;
    }, {});

    const mandatoryCount = requiredDocuments.filter((d) => d.is_mandatory).length;
    const uploadedMandatoryCount = requiredDocuments.filter(
        (d) => d.is_mandatory && uploadedDocuments[d.name]
    ).length;
    const progress = mandatoryCount > 0
        ? Math.round((uploadedMandatoryCount / mandatoryCount) * 100)
        : 0;

    /* ── Pre-upload guard (strict file type / size) ──────────────────── */
    const guardedUpload = (event, doc, oldDocId) => {
        const file = event.files?.[0];
        if (!file) return;

        const typeError = validateFileType(file, doc.allowed_formats);
        const sizeError = !typeError ? validateFileSize(file, doc.max_file_size) : '';
        const err = typeError || sizeError;

        if (err) {
            setDocErrors((prev) => ({ ...prev, [doc.name]: err }));
            event.options?.clear?.();
            return;
        }

        setDocErrors((prev) => ({ ...prev, [doc.name]: '' }));
        onUpload(file, doc, oldDocId);
    };

    /* ── Empty / gated states ────────────────────────────────────────── */
    if (!establishmentTypeChosen) {
        return (
            <Message
                severity="warn"
                text="Please complete Step 3 (Establishment information) first — we'll show the documents you need based on your establishment type."
                className="w-full"
            />
        );
    }

    if (requiredDocuments.length === 0) {
        return (
            <Message
                severity="info"
                text="No documents required for the selected establishment type."
                className="w-full"
            />
        );
    }

    return (
        <div>
            {/* Slim progress strip */}
            <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                <span className="text-700 text-xs font-medium">
                    {uploadedMandatoryCount} of {mandatoryCount} required uploaded
                </span>
                <Tag
                    value={`${progress}%`}
                    severity={progress === 100 ? 'success' : progress > 0 ? 'info' : 'secondary'}
                    className="text-xs"
                />
            </div>
            <ProgressBar
                value={progress}
                showValue={false}
                style={{ height: '4px' }}
                aria-label="Document upload progress"
                className="mb-3"
            />

            {/* Document cards grouped by category as section panels */}
            {Object.entries(groups).map(([category, docs]) => (
                <SectionPanel
                    key={category}
                    icon="pi-folder"
                    title={category}
                    subtitle={`${docs.length} ${docs.length === 1 ? 'document' : 'documents'} · only listed formats accepted`}
                >
                    <div className="grid">
                        {docs.map((doc) => (
                            <DocumentCard
                                key={doc.name}
                                doc={doc}
                                uploaded={uploadedDocuments[doc.name]}
                                isUploading={uploadingDocument === doc.name}
                                isReplacing={replacingDocument === doc.name}
                                error={docErrors[doc.name]}
                                onUpload={(e, oldDocId) => guardedUpload(e, doc, oldDocId)}
                                onReplaceClick={() => onReplaceClick(doc.name)}
                                onCancelReplace={onCancelReplace}
                                onDelete={() => onDelete(doc.name, uploadedDocuments[doc.name]?.id)}
                                onView={() => onView(uploadedDocuments[doc.name]?.document_url)}
                                disabled={saving}
                            />
                        ))}
                    </div>
                </SectionPanel>
            ))}
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════
 * DocumentCard — compact, status-led card.
 *
 *   ┌─────────────────────────────────┐
 *   │ ◯ Document name      [Required] │   ← status icon + title + tag
 *   │   Subtitle (formats / filename) │
 *   │ ─────────────────────────────── │
 *   │   [ Upload ] | [👁 ↻ 🗑]        │   ← action area (one row)
 *   └─────────────────────────────────┘
 *
 *   2 cards per row at md, 3 per row at lg.
 *   ════════════════════════════════════════════════════════════════ */
const DocumentCard = ({
    doc,
    uploaded,
    isUploading,
    isReplacing,
    error,
    onUpload,
    onReplaceClick,
    onCancelReplace,
    onDelete,
    onView,
    disabled,
}) => {
    const status = isUploading ? 'uploading' : uploaded ? 'done' : error ? 'error' : 'pending';
    const inputRef = useRef(null);
    const formatLabel = doc.allowed_formats?.map((f) => f.toUpperCase()).join(' · ');
    const sizeLabel = formatBytes(doc.max_file_size);
    const acceptStr = doc.allowed_formats?.map((f) => `.${f}`).join(',');

    const triggerFileInput = () => {
        if (!disabled) inputRef.current?.click();
    };

    const handleFileChange = (e, oldDocId) => {
        const file = e.target.files?.[0];
        if (!file) return;
        onUpload(
            {
                files: [file],
                options: {
                    clear: () => { if (e.target) e.target.value = ''; },
                },
            },
            oldDocId
        );
        if (e.target) e.target.value = '';
    };

    return (
        <div className="col-12 md:col-6">
            <div
                className={classNames(
                    'surface-card border-round border-1 p-3',
                    status === 'error' ? '' : 'surface-border'
                )}
                style={{
                    borderColor: status === 'done' ? 'var(--green-200)'
                               : status === 'error' ? 'var(--red-200)'
                               : undefined,
                }}
            >
                <div className="flex align-items-center gap-3">
                    {/* Status icon (left) */}
                    <StatusIcon status={status} />

                    {/* Title + subtitle — required indicator stays visible even when title truncates */}
                    <div className="flex-1 min-w-0">
                        <div className="flex align-items-center gap-1">
                            <span className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {doc.label || doc.name}
                            </span>
                            {doc.is_mandatory ? (
                                <span className="text-red-500 flex-shrink-0">*</span>
                            ) : (
                                <span className="text-500 font-normal text-xs flex-shrink-0">(optional)</span>
                            )}
                        </div>
                        <div className="text-500 text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {isUploading
                                ? 'Uploading…'
                                : uploaded
                                    ? uploaded.file_name
                                    : isReplacing
                                        ? 'Pick a new file…'
                                        : `${formatLabel}${sizeLabel ? ` · ${sizeLabel} max` : ''}`}
                        </div>
                    </div>

                    {/* Action (right) */}
                    <div className="flex-shrink-0">
                        {isUploading && (
                            <ProgressSpinner
                                style={{ width: '1.25rem', height: '1.25rem' }}
                                strokeWidth="4"
                            />
                        )}

                        {!isUploading && !isReplacing && !uploaded && (
                            <Button
                                label="Upload"
                                icon="pi pi-upload"
                                size="small"
                                outlined
                                onClick={triggerFileInput}
                                disabled={disabled}
                                aria-label="Upload document"
                                pt={{
                                    // Hide the "Upload" text on mobile — icon-only
                                    // saves horizontal space so the title doesn't
                                    // get squeezed.
                                    label: { className: 'hidden md:inline ml-1' },
                                }}
                            />
                        )}

                        {!isUploading && uploaded && !isReplacing && (
                            <div className="flex align-items-center gap-1">
                                <Button
                                    icon="pi pi-eye"
                                    text
                                    rounded
                                    size="small"
                                    tooltip="View"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={onView}
                                    disabled={disabled}
                                    aria-label="View document"
                                />
                                <Button
                                    icon="pi pi-refresh"
                                    text
                                    rounded
                                    size="small"
                                    tooltip="Replace"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={onReplaceClick}
                                    disabled={disabled}
                                    aria-label="Replace document"
                                />
                                <Button
                                    icon="pi pi-trash"
                                    text
                                    rounded
                                    size="small"
                                    severity="danger"
                                    tooltip="Delete"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={onDelete}
                                    disabled={disabled}
                                    aria-label="Delete document"
                                />
                            </div>
                        )}

                        {isReplacing && (
                            <div className="flex align-items-center gap-1">
                                <Button
                                    label="Choose"
                                    icon="pi pi-upload"
                                    size="small"
                                    outlined
                                    onClick={triggerFileInput}
                                    disabled={disabled}
                                    aria-label="Choose new file"
                                    pt={{
                                        label: { className: 'hidden md:inline ml-1' },
                                    }}
                                />
                                <Button
                                    icon="pi pi-times"
                                    text
                                    rounded
                                    size="small"
                                    severity="secondary"
                                    tooltip="Cancel"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={onCancelReplace}
                                    disabled={disabled}
                                    aria-label="Cancel replace"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Inline error — appears below the row */}
                {error && (
                    <small role="alert" className="p-error text-xs block mt-2">
                        <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                        {error}
                    </small>
                )}

                {/* Hidden file input — triggered by the Upload / Choose button */}
                <input
                    ref={inputRef}
                    type="file"
                    accept={acceptStr}
                    onChange={(e) => handleFileChange(e, isReplacing ? uploaded?.id : null)}
                    style={{ display: 'none' }}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

/* ── Status icon — shown left of every card ───────────────────────── */
const StatusIcon = ({ status }) => {
    const styleByStatus = {
        done: { bg: 'var(--green-50)', icon: 'pi-check-circle', color: 'var(--green-600)' },
        uploading: { bg: 'var(--primary-50)', icon: 'pi-spin pi-spinner', color: 'var(--primary-color)' },
        error: { bg: 'var(--red-50)', icon: 'pi-exclamation-circle', color: 'var(--red-600)' },
        pending: { bg: 'var(--surface-100)', icon: 'pi-file', color: 'var(--text-color-secondary)' },
    };
    const s = styleByStatus[status] || styleByStatus.pending;
    return (
        <div
            className="flex align-items-center justify-content-center border-round flex-shrink-0"
            style={{
                width: '2.25rem',
                height: '2.25rem',
                backgroundColor: s.bg,
                color: s.color,
            }}
        >
            <i className={`pi ${s.icon}`} aria-hidden="true" />
        </div>
    );
};

/* ── runValidations: at least one mandatory doc missing → error ────── */
DocumentsStep.runValidations = (uploadedDocuments, requiredDocuments) => {
    const errors = { documents: '' };
    const missing = requiredDocuments
        .filter((d) => d.is_mandatory && !uploadedDocuments?.[d.name])
        .map((d) => d.label || d.name);
    if (missing.length > 0) {
        errors.documents = `Please upload all required documents: ${missing.join(', ')}.`;
    }
    return errors;
};

DocumentsStep.hasErrors = (errors) => Object.values(errors).some((e) => e !== '');

export default DocumentsStep;
